import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import NextImage from 'next/image';
import html2canvas from 'html2canvas';
import useTranslation from 'next-translate/useTranslation';
import { css, keyframes, Keyframes } from '@emotion/react';
import theme from '@styles/theme';
import {
  CheckStatisticsIcon,
  ChapterBtn,
  ChapterLogoIcon,
  ModalCloseBtn,
} from 'public/assets';
import { ChoiceResult } from '@/customTypes/chatStory';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { alertMessage } from '@slices/alertSlice';
import { RootState } from '@store/store';
import {
  makeChoiceStats,
  touchTabInChoiceStats,
  shareReplayChoiceStats,
} from '@lib/eventManager';
import { setCustomNameToText } from '@service/ChapterService';
import { convertCharName, isMobileDevice } from '@common/utils';

interface IChoiceBar {
  content?: string;
  rate?: number;
  isSelect?: boolean;
  imageUrl?: string;
}

const ChoiceBar = ({
  content,
  rate,
  prevRate,
  isSelect = false,
  imageUrl,
}: IChoiceBar & { prevRate?: number }) => {
  const [barCSS, setBarCSS] = useState<Keyframes>();

  useEffect(() => {
    const barStyle = keyframes`
      0% {
        width: ${prevRate || 0}%;
      }
      100% {
        width: ${rate || 0}%;
      }
  `;
    setBarCSS(barStyle);
  }, [prevRate, rate]);

  return (
    <div css={barContainer(!!imageUrl)}>
      <div className="infoContainer">
        <div className="info">
          <div className="content">
            {imageUrl && (
              <div className="image-box">
                <NextImage
                  src={imageUrl}
                  alt={`${content}image`}
                  objectFit="cover"
                  layout="fill"
                />
              </div>
            )}
            {isSelect && (
              <div className="icon">
                <CheckStatisticsIcon />
              </div>
            )}
            <div
              className={`${
                isSelect ? 'selected-content-text' : 'content-text'
              }`}
            >
              {content}
            </div>
          </div>
          <div className="content-rate">{rate || 0}%</div>
        </div>
      </div>
      <div
        css={css`
          animation: ${barCSS} 0.5s both;
        `}
        className={`${isSelect ? 'selectBar' : 'bar'}`}
      ></div>
    </div>
  );
};

const SelectTab = ({
  title,
  isCheck,
  onCheck,
}: {
  title: string;
  isCheck: boolean;
  onCheck: () => void;
}) => {
  return (
    <div
      css={isCheck ? checkOnText : checkOffText}
      onClick={() => {
        onCheck();
      }}
    >
      {title}
    </div>
  );
};

interface IChoiceStatistics {
  choiceStatistics: {
    choiceResult: ChoiceResult;
    selectedIndex: number;
    isReplay: boolean;
    choiceImageUrls?: string[];
  };
  onClosePopup: () => void;
}

const ChoiceStatistics = ({
  choiceStatistics,
  onClosePopup,
}: IChoiceStatistics) => {
  const { t } = useTranslation();
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const dispatch = useAppDispatch();
  const [statisticType, setStatisticType] = useState<'all' | 'playerClass'>(
    'all',
  );
  const [allItems, setAllItems] = useState<IChoiceBar[]>();
  const [classItems, setClassItems] = useState<IChoiceBar[]>();
  const [playerClassName, setPlayClassName] = useState<string>('');
  const popupRef = useRef<HTMLDivElement>(null);
  const isSafari = useRef<boolean>(false);

  useEffect(() => {
    const agent = navigator.userAgent.toLowerCase();
    isSafari.current = agent.includes('safari') && !agent.includes('chrome');
  }, []);

  useEffect(() => {
    setAllItems(undefined);
    const choiceFromPlayerClasses =
      choiceStatistics.choiceResult.choiceFromPlayerClasses?.[0];

    if (choiceFromPlayerClasses) {
      setPlayClassName(choiceFromPlayerClasses.playerClass.name);
      setStatisticType('playerClass');
    }
    const allResult = new Array(choiceStatistics.choiceResult.numChoice)
      .fill(undefined)
      .map((_, index) => {
        // choiceInfo = ["선택지 내용", "선택지 displayName", "선택지 비율", "이미지 url"];
        const choiceInfo = Object.entries(choiceStatistics.choiceResult)
          .filter(
            (item) =>
              item[0].includes(`choice${index + 1}Content`) ||
              item[0].includes(`choice${index + 1}DisplayName`) ||
              item[0].includes(`choice${index + 1}Rate`) ||
              item[0].includes(`choice${index + 1}ImageUrl`),
          )
          .map((item) => item?.[1]);

        const content = choiceInfo?.[0] as string | undefined;
        const displayName = choiceInfo?.[1] as string | undefined;
        const rate = choiceInfo?.[2] as number | undefined;
        const imageUrl = choiceInfo?.[3] as string | undefined;

        const info: IChoiceBar = {
          // 선택지 내용으로 displayName이 존재하면 displayName보여줌. 만약 displayName 없으면 content 보여줌
          content: displayName
            ? setCustomNameToText({ text: displayName, playChapterInfo })
            : content
            ? setCustomNameToText({ text: content, playChapterInfo })
            : content,
          rate,
          imageUrl,
        };
        return info;
      });
    setAllItems(allResult);

    makeChoiceStats({
      storyName: playChapterInfo?.playChapter.story.name,
      chapterIndex: playChapterInfo?.playChapter.chapterIndex,
      questionName: choiceStatistics.choiceResult.displayName,
      choiceName: allResult?.[choiceStatistics.selectedIndex - 1].content,
      isReplay: choiceStatistics.isReplay,
    });
  }, [choiceStatistics]);

  useEffect(() => {
    if (statisticType === 'playerClass' && !classItems) {
      const classResult = new Array(choiceStatistics.choiceResult.numChoice)
        .fill(undefined)
        .map((_, index) => {
          const choiceNum = index + 1;
          const choiceInfo = Object.entries(choiceStatistics.choiceResult)
            .filter(
              (item) =>
                item[0].includes(`choice${choiceNum}Content`) ||
                item[0].includes(`choice${choiceNum}Rate`) ||
                item[0].includes(`choice${choiceNum}ImageUrl`),
            )
            .map((item) => item?.[1]);

          const content = choiceInfo?.[0] as string | undefined;

          // 특정 class 로 선택되었던 적이 없는 선택지는 값이 내려 오지 않아서, 인덱스로 계산하는 것이 불가하다.
          // 대신 selectedChoice 와 index 로 계산된 choiceNum 의 값을 비교하여 선택지 데이터를 찾는다.
          const rate = (() => {
            const found =
              choiceStatistics.choiceResult.choiceFromPlayerClasses?.find(
                (c) => c.selectedChoice === choiceNum,
              );

            if (!found) {
              return 0;
            }

            return found.choiceRate;
          })();

          const info: IChoiceBar = {
            content: content
              ? setCustomNameToText({ text: content, playChapterInfo })
              : content,
            rate,
            imageUrl: choiceInfo?.[2] as string | undefined,
          };

          return info;
        });

      setClassItems(classResult);
    }
  }, [statisticType]);

  const onClickTab = (tab: 'playerClass' | 'all') => {
    touchTabInChoiceStats({
      storyName: playChapterInfo?.playChapter.story.name,
      chapterIndex: playChapterInfo?.playChapter.chapterIndex,
      questionName: choiceStatistics.choiceResult.displayName,
      choiceName: allItems?.[choiceStatistics.selectedIndex - 1].content,
      tab: tab === 'playerClass' ? 'class' : tab,
      isReplay: choiceStatistics.isReplay,
    });
    setStatisticType(tab);
  };

  const onCaptureImage = () => {
    if (popupRef.current) {
      shareReplayChoiceStats({
        storyName: playChapterInfo?.playChapter.story.name,
        chapterIndex: playChapterInfo?.playChapter.chapterIndex,
        questionName: choiceStatistics.choiceResult.displayName,
        choiceName: allItems?.[choiceStatistics.selectedIndex - 1].content,
        tab: statisticType === 'playerClass' ? 'class' : statisticType,
        isReplay: choiceStatistics.isReplay,
        storyId: playChapterInfo?.playChapter.storyId,
      });
      html2canvas(popupRef.current, {
        allowTaint: true,
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
      })
        .then((canvas) => onSaveImage(canvas.toDataURL('image/png')))
        .catch((error) => {
          dispatch(
            alertMessage({
              message: t('chat_toast_title_retry_error_message_save_image'),
              type: 'warning',
            }),
          );
        });
    }
  };

  const onSaveImage = (dataUrl: string) => {
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = dataUrl;
    link.download = `${choiceStatistics.choiceResult.displayName}.png`;
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div css={popup}>
      {!isSafari.current && !isMobileDevice() && (
        <div className="chapter" onClick={onCaptureImage}>
          <ChapterBtn />
          {t('chat_popup_title_save_statices_image')}
        </div>
      )}
      <div className="content-box" ref={popupRef}>
        <div className="close">
          <ModalCloseBtn width={24} height={24} onClick={onClosePopup} />
        </div>
        <ChapterLogoIcon className="logo" />
        <div className="title">
          {convertCharName(
            choiceStatistics.choiceResult.displayName ?? '',
            playChapterInfo?.mainCharacterName.firstName ?? '',
            playChapterInfo?.mainCharacterName?.lastName ?? '',
          )}
        </div>
        {playerClassName && (
          <div className="select-container">
            <SelectTab
              title={t('chat_screen_title_total_statistics')}
              isCheck={statisticType === 'all'}
              onCheck={() => {
                onClickTab('all');
              }}
            />
            <div className="divide"></div>
            <SelectTab
              title={t('chat_popup_title_statistics_per_class', {
                value: playerClassName,
              })}
              isCheck={statisticType === 'playerClass'}
              onCheck={() => {
                onClickTab('playerClass');
              }}
            />
          </div>
        )}
        <div className="bar-container">
          {statisticType === 'all'
            ? allItems?.map((item, index) => (
                <ChoiceBar
                  key={`${item?.content}_${index}_all`}
                  {...item}
                  prevRate={classItems?.[index]?.rate || 0}
                  isSelect={choiceStatistics.selectedIndex === index + 1}
                />
              ))
            : classItems?.map((item, index) => (
                <ChoiceBar
                  key={`${item?.content}_${index}_class`}
                  {...item}
                  prevRate={allItems?.[index]?.rate || 0}
                  isSelect={choiceStatistics.selectedIndex === index + 1}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

const popup = css`
  position: absolute;
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .chapter {
    padding: 0.75em 0;
    font-weight: 700;
    font-size: 0.82em;
    line-height: 1.125em;
    color: ${theme.colors.white};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.375em;
  }

  .content-box {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0.75rem 0.75rem 1rem 0.75rem;
    border-radius: 0.75em;
    object-fit: cover;
    background-color: ${theme.colors.white};

    .close {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      top: 0;
      left: 0;
      z-index: 1;
      background-color: 'red';
      margin-bottom: 0.625rem;
      cursor: pointer;
    }

    .logo {
      width: 6.125rem;
      height: 1.5rem;
      object-fit: contain;
    }

    .title {
      ${theme.fontV3.body.body03Bold}
      color: ${theme.colors.gray900};
      padding: 0.75rem 0;
    }

    .select-container {
      display: flex;
      align-items: center;
      flex-direction: row;
      margin-bottom: 1rem;
    }

    .bar-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    .divide {
      width: 1px;
      height: 0.875em;
      background-color: ${theme.colors.gray400};
      margin: 0 0.5em;
    }
  }
`;

const checkOnText = css`
  font-weight: 400;
  font-size: 1em;
  margin: 0 0.5em;
  font-weight: bold;
  cursor: pointer;
  color: ${theme.colors.primary500};
`;

const checkOffText = css`
  font-weight: 400;
  font-size: 1em;
  margin: 0 0.5em;
  cursor: pointer;
  color: ${theme.colors.gray600};
`;

const barContainer = (isImage: boolean) => css`
  width: 100%;
  height: ${isImage ? '4.25em' : '2.75em'};
  background: var(--blackA25, rgba(0, 0, 0, 0.25));
  border-radius: 0.25rem;
  display: flex;
  position: relative;

  .infoContainer {
    position: absolute;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    @media screen and (min-width: 700px) {
      width: 100%;
    }
  }

  .info {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 0.75rem;
    font-weight: 700;
    font-size: 1em;
    color: ${theme.colors.white};
  }

  .content {
    width: 100%;
    display: flex;
    align-items: center;

    .content-text {
      flex-shrink: 0;
      max-width: ${isImage ? '60%' : '80%'};
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      display: -webkit-box;
      text-align: center;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      ${theme.fontV3.body.body05Reg}
      color: var(--whiteA90, rgba(255, 255, 255, 0.90));

      @media screen and (min-width: 700px) {
        line-height: 1.25em;
        font-size: 1em;
        max-width: ${isImage ? '80%' : '100%'};
      }

      .selected-content-text {
        ${theme.fontV3.body.body05Bold}
      }
    }
  }

  .content-rate {
    flex-shrink: 0;
    ${theme.fontV3.body.body05Bold}
    margin-left: 0.75rem;
  }

  .bar {
    height: 100%;
    background-color: var(--blackA90, rgba(0, 0, 0, 0.9));
    border-radius: 0.25rem;
  }

  .selectBar {
    height: 100%;
    background-color: ${theme.colors.primary500};
    border-radius: 0.25em;
  }

  .icon {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    margin-right: 0.375rem;
  }

  .image-box {
    position: relative;
    height: 2.75em;
    width: 2.75em;
    margin: 0.75em;
    border-radius: 4px;
    flex-shrink: 0;
    object-fit: cover;
    object-position: 50%;
  }
`;

export default ChoiceStatistics;
